import React, { useState, useEffect } from 'react';
import { Menu, X, Globe, Phone, MapPin, Clock } from 'lucide-react';
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { translations } from '../data/translations';

export const Navbar = ({ lang, setLang }) => {
  const t = translations[lang].nav;
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const NavLinks = ({ mobile = false, closeMenu }) => (
    <>
      {['home', 'about', 'menu', 'contact'].map((key) => (
        <button
          key={key}
          onClick={() => {
            scrollToSection(key);
            if (mobile && closeMenu) closeMenu();
          }}
          className={`${
            mobile ? 'text-2xl py-4 font-semibold' : 'text-sm font-medium uppercase tracking-wider hover:text-red-500 transition-colors'
          } ${isScrolled || mobile ? 'text-white' : 'text-white'} `}
        >
          {t[key]}
        </button>
      ))}
    </>
  );

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/95 backdrop-blur-md shadow-lg py-3' : 'bg-gradient-to-b from-black/80 to-transparent py-6'
      }`}
    >
      <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => scrollToSection('home')}>
          <div className="h-12 w-12 flex items-center justify-center overflow-hidden rounded-full bg-white p-1">
             <img src="https://customer-assets.emergentagent.com/job_7fc4b9e7-8343-429e-97eb-b7f84a7435e7/artifacts/vlu7m02g_buffalo%20logo.svg" alt="Buffalo Logo" className="w-full h-full object-contain" />
          </div>
          <div className="flex flex-col">
            <span className="text-xl md:text-2xl font-bold text-white tracking-tighter">BUFFALO</span>
            <span className="text-xs text-red-500 font-bold tracking-[0.2em] -mt-1">GRILL HOUSE</span>
          </div>
        </div>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          <NavLinks />
          <Button 
            variant="outline" 
            className="border-red-600 text-red-500 hover:bg-red-600 hover:text-white transition-colors uppercase font-bold text-xs tracking-wider"
            onClick={() => window.open('https://www.restaurantlogin.com/api/res/y_pz_z_x', '_blank')}
          >
            {t.book}
          </Button>

          {/* Language Switcher */}
          <div className="flex items-center gap-2 text-white/80 border-l border-white/20 pl-4">
            <Globe className="w-4 h-4" />
            <select 
              value={lang} 
              onChange={(e) => setLang(e.target.value)}
              className="bg-transparent border-none outline-none text-sm font-medium cursor-pointer focus:ring-0 text-black md:text-white"
            >
              <option value="fi" className="text-black">FI</option>
              <option value="sv" className="text-black">SE</option>
              <option value="en" className="text-black">EN</option>
            </select>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className="md:hidden flex items-center gap-4">
           {/* Mobile Lang Switcher */}
           <div className="flex items-center gap-2">
             <select 
               value={lang} 
               onChange={(e) => setLang(e.target.value)}
               className="bg-transparent text-white font-bold text-sm border border-white/30 rounded px-1"
             >
               <option value="fi" className="text-black">FI</option>
               <option value="sv" className="text-black">SE</option>
               <option value="en" className="text-black">EN</option>
             </select>
           </div>

          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-zinc-950 border-zinc-800 text-white w-[300px]">
              <div className="flex flex-col h-full pt-10 items-center">
                <NavLinks mobile closeMenu={() => document.body.click()} />
                <div className="mt-8 flex flex-col gap-4 w-full">
                  <Button 
                    className="w-full bg-red-600 hover:bg-red-700 text-white"
                    onClick={() => window.open('https://www.restaurantlogin.com/api/res/y_pz_z_x', '_blank')}
                  >
                    {t.book}
                  </Button>
                  <Button 
                    className="w-full bg-white text-black hover:bg-gray-200"
                    onClick={() => window.open('https://www.restaurantlogin.com/api/fb/y_pz_z_x', '_blank')}
                  >
                    {t.order}
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};
